/**
 * BadgeGrid Component
 *
 * Displays all available badges with earned status.
 * Can be used to show all badges or just a user's collection.
 */

import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import api from '../../services/api'
import BadgeCard from './BadgeCard'
import { createLogger } from '../../utils/logger'

const logger = createLogger('BadgeGrid')

const BadgeGrid = ({ userId, showAll = false, maxBadges = null }) => {
  const [allBadges, setAllBadges] = useState([])
  const [userBadges, setUserBadges] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchBadges()
  }, [userId])

  const fetchBadges = async () => {
    try {
      setLoading(true)
      setError(null)

      const [allResponse, userResponse] = await Promise.all([
        showAll ? api.get('/badges') : Promise.resolve({ data: { data: { badges: [] } } }),
        api.get('/badges/me')
      ])

      setAllBadges(allResponse.data.data?.badges || [])
      setUserBadges(userResponse.data.data?.badges || [])
    } catch (err) {
      logger.error('Failed to fetch badges:', err)
      setError('Failed to load badges')
    } finally {
      setLoading(false)
    }
  }

  // Create a map of earned badge codes
  const earnedBadgeCodes = new Set(userBadges.map((ub) => ub.badge?.code))
  const earnedBadgeMap = userBadges.reduce((acc, ub) => {
    if (ub.badge?.code) {
      acc[ub.badge.code] = ub
    }
    return acc
  }, {})

  // If showing all badges, combine with user badges
  const displayBadges = showAll
    ? allBadges.map((badge) => ({
        badge,
        earnedAt: earnedBadgeMap[badge.code]?.earnedAt,
        locked: !earnedBadgeCodes.has(badge.code)
      }))
    : userBadges.map((ub) => ({
        badge: ub.badge,
        earnedAt: ub.earnedAt,
        locked: false
      }))

  // Apply max limit if specified
  const limitedBadges = maxBadges ? displayBadges.slice(0, maxBadges) : displayBadges

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center text-red-500 dark:text-red-400 py-4">
        <p>{error}</p>
      </div>
    )
  }

  if (limitedBadges.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        <svg
          className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500 mb-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
          />
        </svg>
        <p>No badges yet</p>
        <p className="text-sm">Complete challenges to earn achievements!</p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
      {limitedBadges.map((item, index) => (
        <BadgeCard
          key={item.badge?.id || index}
          badge={item.badge}
          earnedAt={item.earnedAt}
          locked={item.locked}
        />
      ))}
    </div>
  )
}

BadgeGrid.propTypes = {
  /** User ID to fetch badges for (uses authenticated user if not provided) */
  userId: PropTypes.string,
  /** If true, shows all available badges with locked state for unearned ones */
  showAll: PropTypes.bool,
  /** Maximum number of badges to display (null for unlimited) */
  maxBadges: PropTypes.number
}

BadgeGrid.defaultProps = {
  userId: null,
  showAll: false,
  maxBadges: null
}

export default BadgeGrid
